import { useEffect, useState } from "react";
import { Plus, Trash2, Save, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { PageTitle, AdminButton, Field, useList, saveDoc } from "@/components/admin/kit";
import { useUnsaved, UnsavedBadge, same } from "@/lib/unsaved";

const BLANK = { name: "", title: "REALTOR\u00ae", brokerage: "", phone: "", email: "", website: "", photo_url: "", logo_url: "", bio: "" };

function AgentCard({ agent, onSaved }) {
  const [a, setA] = useState(agent);
  useEffect(() => { setA(agent); }, [agent]);
  const dirty = !same(a, agent);
  useUnsaved(dirty);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const id = a.id || "new";
  const save = async () => { if (await saveDoc("/admin/agents", a)) onSaved(); };
  const del = async () => { if (window.confirm(`Remove ${a.name}? Tours using them will no longer show a realtor.`)) { await api.delete(`/admin/agents/${a.id}`); onSaved(); } };
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={`agent-card-${id}`}>
      <div className="flex flex-wrap gap-6">
        <div className="flex gap-4">
          <Field label="Photo"><ImageUpload value={a.photo_url} onChange={(v) => setA({ ...a, photo_url: v })} testid={`agent-photo-${id}`} /></Field>
          <Field label="Brokerage logo"><ImageUpload value={a.logo_url} onChange={(v) => setA({ ...a, logo_url: v })} testid={`agent-logo-${id}`} /></Field>
        </div>
        <div className="flex-1 min-w-[16rem] grid sm:grid-cols-2 gap-3">
          <Field label="Name"><Input value={a.name} maxLength={80} onChange={set("name")} placeholder="Jane Smith" data-testid={`agent-name-${id}`} /></Field>
          <Field label="Title"><Input value={a.title} maxLength={80} onChange={set("title")} placeholder="REALTOR®" data-testid={`agent-title-${id}`} /></Field>
          <Field label="Brokerage / business" className="sm:col-span-2"><Input value={a.brokerage} maxLength={120} onChange={set("brokerage")} placeholder="e.g. RE/MAX House of Real Estate" data-testid={`agent-brokerage-${id}`} /></Field>
          <Field label="Phone"><Input value={a.phone} maxLength={40} onChange={set("phone")} placeholder="403-555-0123" data-testid={`agent-phone-${id}`} /></Field>
          <Field label="Email"><Input type="email" value={a.email} maxLength={120} onChange={set("email")} placeholder="jane@brokerage.ca" data-testid={`agent-email-${id}`} /></Field>
          <Field label="Website" className="sm:col-span-2"><Input value={a.website} maxLength={300} onChange={set("website")} placeholder="https://..." data-testid={`agent-website-${id}`} /></Field>
          <Field label="Short bio (optional)" className="sm:col-span-2"><Textarea rows={2} value={a.bio} maxLength={600} onChange={set("bio")} data-testid={`agent-bio-${id}`} /></Field>
        </div>
      </div>
      <div className="flex gap-2 justify-end mt-4 items-center">
        <UnsavedBadge show={dirty} />
        {a.id && <AdminButton variant="danger" onClick={del} data-testid={`agent-delete-${id}`}><Trash2 className="w-4 h-4" />Remove</AdminButton>}
        <AdminButton onClick={save} disabled={!a.name.trim()} data-testid={`agent-save-${id}`}><Save className="w-4 h-4" />Save</AdminButton>
      </div>
    </div>
  );
}

export default function AdminRealtors() {
  const [agents, reload] = useList("/admin/agents");
  const [adding, setAdding] = useState(false);
  if (!agents) return null;
  return (
    <div>
      <PageTitle title="Realtors" sub="Add each realtor once, then choose who presents each tour in its setup (step 4)" right={<AdminButton onClick={() => setAdding(true)} data-testid="agent-new"><Plus className="w-4 h-4" />Add realtor</AdminButton>} />
      <div className="space-y-5">
        {adding && <AgentCard agent={{ ...BLANK, order: agents.length }} onSaved={() => { setAdding(false); reload(); }} />}
        {!adding && agents.length === 0 && <div className="rounded-3xl border border-dashed p-10 text-center text-slate-500" data-testid="agents-empty"><UserRound className="w-8 h-8 mx-auto mb-2" />No realtors yet. Add one to show a "Presented by" card on your tours.</div>}
        {agents.map((a) => <AgentCard key={a.id} agent={a} onSaved={reload} />)}
      </div>
    </div>
  );
}
